<template>
  <section class="ground-station-shop-selector" :class="{ popup, inline }">
    <div v-if="!inline" class="panel-heading gs-section-heading"><span>{{ $t("商铺") }}</span><GroundStationStatusBadge :tone="shopOpenBadge.tone">{{ $t(shopOpenBadge.text) }}</GroundStationStatusBadge></div>
    <div class="shop-select-wrap">
      <SelectionOptionIcon class="selected-avatar" :src="selectedShopLogo" :alt="$t(selectedShopName)" :text="$t(!value ? 'ALL' : '')" />
      <UiSelect
        :value="value"
        :aria-label="$t('工作台商铺筛选')"
        :placeholder="$t('所有商铺')"
        popper-class="ground-station-shop-menu"
        @input="$emit('input', $event)"
      >
        <el-option :label="$t('所有商铺')" value="">
          <span class="shop-option"><SelectionOptionIcon text="ALL" /><span>{{ $t("所有商铺") }}</span></span>
        </el-option>
        <el-option
          v-for="shop in shops"
          :key="getShopId(shop)"
          :label="shop.name || '未命名商铺'"
          :value="getShopId(shop)"
        >
          <span class="shop-option">
            <SelectionOptionIcon :src="getShopLogo(shop)" :alt="shop.name || '未命名商铺'" />
            <span>{{ shop.name || '未命名商铺' }}</span>
          </span>
        </el-option>
      </UiSelect>
    </div>
  </section>
</template>

<script>
import GroundStationStatusBadge from '@/components/ground-station/GroundStationStatusBadge'
import SelectionOptionIcon from '@/components/SelectionOptionIcon'
import { getShopOpenState } from '@/utils/operationalStatus'

export default {
  name: 'GroundStationShopSelector',
  components: { GroundStationStatusBadge, SelectionOptionIcon },
  data () {
    return { statusNow: new Date(), statusTimer: null }
  },
  props: {
    value: { type: [String, Number], default: '' },
    shops: { type: Array, default: () => [] },
    popup: { type: Boolean, default: false },
    inline: { type: Boolean, default: false }
  },
  computed: {
    selectedShop () { return this.shops.find(shop => this.getShopId(shop) === String(this.value)) || null },
    selectedShopLogo () { return this.getShopLogo(this.selectedShop) },
    selectedShopName () { return this.selectedShop?.name || '所有商铺' },
    shopOpenBadge () {
      if (this.selectedShop) {
        const state = getShopOpenState(this.selectedShop, this.statusNow)
        return { text: state.isOpen ? '营业中' : '已打烊', tone: state.isOpen ? 'accent' : 'danger' }
      }
      const openCount = this.shops.filter(shop => getShopOpenState(shop, this.statusNow).isOpen).length
      return {
        text: `${openCount} 家在营业`,
        tone: openCount ? 'accent' : (this.shops.length ? 'danger' : 'muted')
      }
    }
  },
  watch: {
    shops: {
      immediate: true,
      handler: 'validateSelection'
    },
    value: 'validateSelection'
  },
  mounted () {
    this.statusTimer = setInterval(() => { this.statusNow = new Date() }, 60000)
  },
  beforeDestroy () {
    clearInterval(this.statusTimer)
  },
  methods: {
    getShopId (shop) { return String((shop && (shop.shop_id || shop.id)) || '') },
    getShopLogo (shop) {
      if (!shop) return ''
      if (Array.isArray(shop.logo)) return shop.logo[0] || ''
      return typeof shop.logo === 'string' ? shop.logo : ''
    },
    validateSelection () {
      if (!this.value || !this.shops.length) return
      if (!this.shops.some(shop => this.getShopId(shop) === String(this.value))) this.$emit('input', '')
    }
  }
}
</script>

<style lang="scss" scoped>
.ground-station-shop-selector {
  width: 100%;
  display: grid;
  padding: 0 var(--ui-space-sm) var(--ui-space-md);
  box-sizing: border-box;
}

.ground-station-shop-selector.popup {
  padding-top: 2px;
}

.ground-station-shop-selector.inline { width: 144px; padding: 0; margin-left: var(--ui-space-sm); flex-shrink: 0; }
@media (max-width: 700px) {
  .ground-station-shop-selector.inline { width: 104px; margin-left: var(--ui-space-xs); }
  .ground-station-shop-selector.inline .selected-avatar { display: none; }
  .ground-station-shop-selector.inline .shop-select-wrap ::v-deep .el-input__inner { padding-left: var(--ui-space-sm); padding-right: 30px; }
}

.panel-heading {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2px;
  color: var(--ui-text-secondary);
  font-size: var(--gs-font-heading, 12px);
  letter-spacing: 1.3px;
}

.shop-select-wrap {
  width: 100%;
  position: relative;
}

.shop-select-wrap .el-select {
  width: 100%;
}

.selected-avatar {
  position: absolute;
  top: 8px;
  left: 9px;
  z-index: 2;
  pointer-events: none;
}

.shop-select-wrap ::v-deep .el-input__inner {
  padding: 0 38px 0 42px;
}
</style>

<style lang="scss">
.ground-station-shop-menu {
  max-height: 280px;
  overflow: hidden;
}

.ground-station-shop-menu .el-select-dropdown__list {
  padding: var(--ui-space-xs) 0 0 !important;
}

.ground-station-shop-menu .el-select-dropdown__list::after {
  content: '';
  height: 6px;
  display: block;
  pointer-events: none;
}

.ground-station-shop-menu .el-select-dropdown__wrap {
  max-height: 268px;
  margin-right: 0 !important;
  margin-bottom: 0 !important;
  overflow-x: hidden;
  scrollbar-width: none;
}

.ground-station-shop-menu .el-select-dropdown__wrap::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.ground-station-shop-menu .el-scrollbar__bar.is-vertical {
  display: none;
}

.ground-station-shop-menu .shop-option {
  display: flex;
  align-items: center;
  gap: var(--ui-space-sm);
}

.ground-station-shop-menu .el-select-dropdown__item {
  height: 36px;
  line-height: 36px;
}

</style>
